import { Action, createReducer, on } from '@ngrx/store';
import { ILoaderActionPayload, LoadingState } from '../models/loader.model';
import { agentActions, clusterActions } from './agent.actions';
import {
  AgentClusterItem,
  AgentClusterListResponse,
  AgentItem,
  AgentListResponse,
  AgentsAndClustersStatsItem,
  CounterResponse,
  HeartbeatResponse,
} from '../../../api/models';

interface IpRange {
  startIp: string;
  endIp: string;
}

export interface ScanRange {
  subnets: string[];
  ipRanges: IpRange[];
  singleIps: string[];
}

export interface AgentState {
  //agent
  agentList: {
    state: ILoaderActionPayload;
    data?: AgentListResponse;
  };
  singleAgent: {
    state: ILoaderActionPayload;
    data?: AgentItem & { scanRange: ScanRange };
  };

  createAgent: {
    state: ILoaderActionPayload;
    data?: AgentItem;
  };

  updateAgent: {
    state: ILoaderActionPayload;
    data?: AgentItem;
  };

  forceDeleteAgent: {
    state: ILoaderActionPayload;
    data?: AgentItem;
  };

  onOffAgent: {
    state: ILoaderActionPayload;
    data?: AgentItem;
  };
  onOffMultipleAgents: {
    state: ILoaderActionPayload;
    data?: CounterResponse;
  };
  synchronize: {
    state: ILoaderActionPayload;
    data?: AgentItem;
  };
  synchronizeMultiple: {
    state: ILoaderActionPayload;
    data?: CounterResponse;
  };

  heartbeatList: {
    state: ILoaderActionPayload;
    data?: HeartbeatResponse;
  };

  agentsCounterStats: {
    state: ILoaderActionPayload;
    data?: AgentsAndClustersStatsItem;
  };

  //cluster
  clusterList: {
    state: ILoaderActionPayload;
    data?: AgentClusterListResponse;
  };
  singleCluster: {
    state: ILoaderActionPayload;
    data?: AgentClusterItem;
  };

  createCluster: {
    state: ILoaderActionPayload;
    data?: AgentClusterItem;
  };

  updateCluster: {
    state: ILoaderActionPayload;
    data?: AgentClusterItem;
  };

  clustersCounterStats: {
    state: ILoaderActionPayload;
    data?: AgentsAndClustersStatsItem;
  };
}
const initialState: AgentState = {
  agentList: {
    state: {
      value: LoadingState.NONE,
    },
  },
  singleAgent: {
    state: {
      value: LoadingState.NONE,
    },
  },

  createAgent: {
    state: {
      value: LoadingState.NONE,
    },
  },

  updateAgent: {
    state: {
      value: LoadingState.NONE,
    },
  },

  forceDeleteAgent: {
    state: {
      value: LoadingState.NONE,
    },
  },

  onOffAgent: {
    state: {
      value: LoadingState.NONE,
    },
  },
  onOffMultipleAgents: {
    state: {
      value: LoadingState.NONE,
    },
  },
  synchronize: {
    state: {
      value: LoadingState.NONE,
    },
  },
  synchronizeMultiple: {
    state: {
      value: LoadingState.NONE,
    },
  },

  heartbeatList: {
    state: {
      value: LoadingState.NONE,
    },
  },

  agentsCounterStats: {
    state: {
      value: LoadingState.NONE,
    },
  },

  clusterList: {
    state: {
      value: LoadingState.NONE,
    },
  },
  singleCluster: {
    state: {
      value: LoadingState.NONE,
    },
  },
  createCluster: {
    state: {
      value: LoadingState.NONE,
    },
  },
  updateCluster: {
    state: {
      value: LoadingState.NONE,
    },
  },
  clustersCounterStats: {
    state: {
      value: LoadingState.NONE,
    },
  },
};

const reducer = createReducer(
  initialState,
  // load agents
  on(agentActions.loadAgentList, (state) => ({
    ...state,
    agentList: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.loadAgentListDone, (state, props) => ({
    ...state,
    agentList: { state: props.state, data: props.data },
  })),
  on(agentActions.loadAgentListSetState, (state, props) => ({
    ...state,
    agentList: { state: props.state },
  })),

  //load one agent
  on(agentActions.loadOne, (state) => ({
    ...state,
    singleAgent: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.doneOne, (state, props) => ({
    ...state,
    singleAgent: { state: props.state, data: props.data },
  })),
  on(agentActions.loadOneSetState, (state, props) => ({
    ...state,
    singleAgent: { state: props.state },
  })),

  // create agent
  on(agentActions.createAgent, (state) => ({
    ...state,
    createAgent: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.createAgentDone, (state, props) => ({
    ...state,
    createAgent: { state: props.state, data: props.data },
  })),
  on(agentActions.createAgentSetState, (state, props) => ({
    ...state,
    createAgent: { state: props.state },
  })),

  //update agent
  on(agentActions.updateAgent, (state) => ({
    ...state,
    updateAgent: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.updateAgentDone, (state, props) => ({
    ...state,
    updateAgent: { state: props.state, data: props.data },
  })),
  on(agentActions.updateAgentSetState, (state, props) => ({
    ...state,
    updateAgent: { state: props.state },
  })),

  //force delete agent
  on(agentActions.forceDeleteAgent, (state) => ({
    ...state,
    forceDeleteAgent: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.forceDeleteAgentDone, (state, props) => ({
    ...state,
    forceDeleteAgent: { state: props.state, data: props.data },
  })),
  on(agentActions.forceDeleteAgentSetState, (state, props) => ({
    ...state,
    forceDeleteAgent: { state: props.state },
  })),

  // on off agent
  on(agentActions.onOffAgent, (state) => ({
    ...state,
    onOffAgent: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.onOffAgentDone, (state, props) => ({
    ...state,
    onOffAgent: { state: props.state, data: props.data },
    agentList: {
      ...state.agentList,
      data: {
        ...state.agentList.data,
        list:
          state.agentList.data?.list?.map((item) => (item.id === props.data?.id ? { ...item, ...props.data } : item)) ??
          [],
        count: state.agentList.data?.count ?? 0,
      },
    },
  })),
  on(agentActions.onOffAgentSetState, (state, props) => ({
    ...state,
    onOffAgent: { state: props.state },
  })),
  // on/off multiple agents
  on(agentActions.onOffMultipleAgents, (state) => ({
    ...state,
    onOffMultipleAgents: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.onOffMultipleAgentsDone, (state, props) => ({
    ...state,
    onOffMultipleAgents: { state: props.state, data: props.data },
  })),
  on(agentActions.onOffMultipleAgentsSetState, (state, props) => ({
    ...state,
    onOffMultipleAgents: { state: props.state },
  })),
  // synchronize
  on(agentActions.synchronize, (state) => ({
    ...state,
    synchronize: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.synchronizeDone, (state, props) => ({
    ...state,
    synchronize: { state: props.state, data: props.data },
    agentList: {
      ...state.agentList,
      data: {
        ...state.agentList.data,
        list:
          state.agentList.data?.list?.map((item) => (item.id === props.data?.id ? { ...item, ...props.data } : item)) ??
          [],
        count: state.agentList.data?.count ?? 0,
      },
    },
  })),
  on(agentActions.synchronizeSetState, (state, props) => ({
    ...state,
    synchronize: { state: props.state },
  })),
  // synchronize multiple agents
  on(agentActions.synchronizeMultiple, (state) => ({
    ...state,
    synchronizeMultiple: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.synchronizeMultipleDone, (state, props) => ({
    ...state,
    synchronizeMultiple: { state: props.state, data: props.data },
  })),
  on(agentActions.synchronizeMultipleSetState, (state, props) => ({
    ...state,
    synchronizeMultiple: { state: props.state },
  })),

  // heartbeat list
  on(agentActions.loadHeartbeatList, (state) => ({
    ...state,
    heartbeatList: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.loadHeartbeatListDone, (state, props) => ({
    ...state,
    heartbeatList: { state: props.state, data: props.data },
  })),
  on(agentActions.loadHeartbeatListSetState, (state, props) => ({
    ...state,
    heartbeatList: { state: props.state },
  })),

  //agents counter stats
  on(agentActions.agentsCounterStats, (state) => ({
    ...state,
    agentsCounterStats: { state: { value: LoadingState.LOADING } },
  })),
  on(agentActions.agentsCounterStatsDone, (state, props) => ({
    ...state,
    agentsCounterStats: { state: props.state, data: props.data },
  })),
  on(agentActions.agentsCounterStatsSe, (state, props) => ({
    ...state,
    agentsCounterStats: { state: props.state },
  })),

  // load cluster list
  on(clusterActions.loadClusterList, (state) => ({
    ...state,
    clusterList: { state: { value: LoadingState.LOADING } },
  })),
  on(clusterActions.loadClusterListDone, (state, props) => ({
    ...state,
    clusterList: { state: props.state, data: props.data },
  })),
  on(clusterActions.loadClusterListSetState, (state, props) => ({
    ...state,
    clusterList: { state: props.state },
  })),

  //load one cluster
  on(clusterActions.loadOneCluster, (state) => ({
    ...state,
    singleCluster: { state: { value: LoadingState.LOADING } },
  })),
  on(clusterActions.loadOneClusterDone, (state, props) => ({
    ...state,
    singleCluster: { state: props.state, data: props.data },
  })),
  on(clusterActions.loadOneClusterSetState, (state, props) => ({
    ...state,
    singleCluster: { state: props.state },
  })),

  // create cluster
  on(clusterActions.createCluster, (state) => ({
    ...state,
    createCluster: { state: { value: LoadingState.LOADING } },
  })),
  on(clusterActions.createClusterDone, (state, props) => ({
    ...state,
    createCluster: { state: props.state, data: props.data },
  })),
  on(clusterActions.createClusterSetState, (state, props) => ({
    ...state,
    createCluster: { state: props.state },
  })),

  //update cluster
  on(clusterActions.updateCluster, (state) => ({
    ...state,
    updateCluster: { state: { value: LoadingState.LOADING } },
  })),
  on(clusterActions.updateClusterDone, (state, props) => ({
    ...state,
    updateCluster: { state: props.state, data: props.data },
  })),
  on(clusterActions.updateClusterSetState, (state, props) => ({
    ...state,
    updateCluster: { state: props.state },
  })),

  //clusters counter stats
  on(clusterActions.clustersCounterStats, (state) => ({
    ...state,
    clustersCounterStats: { state: { value: LoadingState.LOADING } },
  })),
  on(clusterActions.clustersCounterStatsDone, (state, props) => ({
    ...state,
    clustersCounterStats: { state: props.state, data: props.data },
  })),
  on(clusterActions.clustersCounterStatsSetState, (state, props) => ({
    ...state,
    clustersCounterStats: { state: props.state },
  })),
);

export function agentReducer(state: AgentState | undefined, action: Action) {
  return reducer(state, action);
}
